import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { FiArrowRight, FiUser, FiPackage, FiVideo } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useDemoMode } from '@/context/DemoModeContext';
import { Button } from "@/components/ui/button";

interface Creator {
  id: string;
  username: string;
  walletAddress: string;
  bio?: string;
  avatarUrl?: string;
  coverUrl?: string;
  dropsCount: number;
  streamsCount: number;
  followersCount: number;
}

const FeaturedCreators = () => {
  const [hoveredCreator, setHoveredCreator] = useState<string | null>(null);
  const { isDemoMode } = useDemoMode();

  // Fetch featured creators
  const { data: creators, isLoading } = useQuery({
    queryKey: ['featuredCreators', isDemoMode],
    queryFn: async () => {
      // Import demo data
      const { demoCreators } = await import('@/services/demoData');
      
      // Map the demo creators to match the Creator interface
      const mappedCreators = demoCreators.map(creator => ({
        id: creator.id,
        username: creator.username,
        walletAddress: creator.walletAddress,
        bio: creator.bio,
        avatarUrl: creator.profileImageUrl,
        coverUrl: creator.coverImageUrl,
        dropsCount: creator.nftCount,
        streamsCount: creator.streamCount,
        followersCount: creator.followers || 0
      }));
      
      // Always use demo data in demo mode
      if (isDemoMode) {
        console.log('Using demo creators data (Demo Mode)');
        return mappedCreators;
      }
      
      try {
        // Try to fetch from API with a short timeout
        const response = await api.get<Creator[]>('/api/users/role/creators?featured=true&limit=4', {
          timeout: 5000 // 5 second timeout
        });
        
        if (response.data && response.data.data && response.data.data.length > 0) {
          return response.data.data;
        } else {
          console.log('API returned empty data, falling back to demo creators data');
          return mappedCreators;
        }
      } catch (error) {
        console.log('API error, falling back to demo creators data', error);
        // Always return demo data on error
        return mappedCreators;
      }
    },
    retry: 1, // Only retry once
    retryDelay: 1000, // Wait 1 second before retrying
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    refetchOnWindowFocus: false // Don't refetch when window regains focus
  });

  return (
    <section className="section bg-dark-900 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-primary-500/5 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-secondary-500/5 rounded-full filter blur-3xl"></div>
      </div>
      
      <div className="container-custom relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">Featured Creators</h2>
            <p className="text-gray-400 max-w-xl">
              Discover talented artists and content creators shaping the future of digital media.
            </p>
          </div>
          <Link to="/explore?tab=creators">
            <Button variant="outline" className="shadow-3d group">
              View All Creators
              <FiArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>

        {isLoading ? (
          // Loading skeleton
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="bg-dark-800 rounded-2xl animate-pulse h-80"></div>
            ))}
          </div>
        ) : creators && creators.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {creators.map((creator) => (
              <CreatorCard 
                key={creator.id} 
                creator={creator} 
                isHovered={hoveredCreator === creator.id}
                onHover={() => setHoveredCreator(creator.id)}
                onLeave={() => setHoveredCreator(null)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-dark-800 rounded-2xl">
            <FiUser size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400 text-lg">No featured creators available</p>
          </div>
        )}
      </div>
    </section>
  );
};

interface CreatorCardProps {
  creator: Creator;
  isHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
}

const CreatorCard = ({ creator, isHovered, onHover, onLeave }: CreatorCardProps) => {
  return (
    <Link 
      to={`/creator/${creator.id}`}
      className="block h-full"
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      <motion.div 
        className="card card-hover shadow-3d h-full flex flex-col relative overflow-hidden bg-dark-800/50 backdrop-blur-sm"
        whileHover={{ y: -8, scale: 1.02 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        {/* Cover Image */}
        <div className="h-32 w-full rounded-t-xl overflow-hidden relative">
          <div className="w-full h-full bg-gradient-to-r from-primary-600/30 to-secondary-600/30 relative">
            {creator.coverUrl && (
              <img 
                src={creator.coverUrl} 
                alt={`${creator.username}'s cover`} 
                className="w-full h-full object-cover absolute mix-blend-overlay transition-transform duration-300 hover:scale-110"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-dark-800/80 to-transparent"></div>
        </div>
        
        {/* Avatar and Info */}
        <div className="px-4 sm:px-6 pt-0 pb-4 sm:pb-6 flex-1 flex flex-col relative z-10">
          <div className="flex items-end -mt-8 mb-4">
            <motion.div 
              className="w-16 h-16 rounded-xl bg-dark-700 border-4 border-dark-800 overflow-hidden shadow-3d"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              {creator.avatarUrl ? (
                <img 
                  src={creator.avatarUrl} 
                  alt={creator.username} 
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  onError={(e) => {
                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(creator.username)}&background=7c3aed&color=fff`;
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-secondary-600 text-white text-xl font-bold">
                  {creator.username.charAt(0).toUpperCase()}
                </div>
              )}
            </motion.div>
            <div className="ml-3 flex-1 min-w-0">
              <h3 className="text-lg font-bold text-white text-shadow truncate">{creator.username}</h3>
              <p className="text-xs text-gray-400 truncate">{creator.walletAddress}</p>
            </div>
          </div>
          
          {/* Bio */}
          <p className="text-gray-400 text-sm mb-4 line-clamp-2 flex-grow">
            {creator.bio || "This creator hasn't added a bio yet."}
          </p>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <motion.div 
              className="bg-dark-800/80 backdrop-blur-sm rounded-lg p-2 border border-dark-700 shadow-inner-3d"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <FiPackage className="mx-auto text-primary-500 mb-1" />
              <p className="text-xs text-gray-400">Drops</p>
              <p className="text-sm font-medium text-shadow">{creator.dropsCount}</p>
            </motion.div>
            <motion.div 
              className="bg-dark-800/80 backdrop-blur-sm rounded-lg p-2 border border-dark-700 shadow-inner-3d"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <FiVideo className="mx-auto text-secondary-500 mb-1" />
              <p className="text-xs text-gray-400">Streams</p>
              <p className="text-sm font-medium text-shadow">{creator.streamsCount}</p>
            </motion.div>
            <motion.div 
              className="bg-dark-800/80 backdrop-blur-sm rounded-lg p-2 border border-dark-700 shadow-inner-3d"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <FiUser className="mx-auto text-accent-500 mb-1" />
              <p className="text-xs text-gray-400">Followers</p>
              <p className="text-sm font-medium text-shadow">{creator.followersCount}</p>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

export default FeaturedCreators;